import React, { useState, useEffect } from 'react';
import { Settings, User, Palette, Save, Printer, X } from 'lucide-react';
import API_URL from '../../config/api';
import axios from 'axios';
import CustomAlert from './shared/CustomAlert';

const Configuracion = ({ token, clientData, onProfileUpdate, onBgColorChange }) => {
 // ⭐ Cuenta CRM-only (ver DashboardPOS.js): oculta los tabs que son de POS
 // puro (Pedidos = timers/sonido de cocina, Impresión = ticket/cierre de
 // caja) -- no aplican a un negocio que solo tiene el CRM, sin punto de venta.
 const isCrmOnly = clientData?.productType === 'crm';
 const [activeTab, setActiveTab] = useState('perfil'); // perfil, colores, pedidos
 const [loading, setLoading] = useState(false);
 const [customAlert, setCustomAlert] = useState(null);

 const showAlert = (type, title, message) => {
 setCustomAlert({ type, title, message });
 };

 // Estado del perfil
 const [profileData, setProfileData] = useState({
 name: clientData?.name || '',
 email: clientData?.email || '',
 phone: clientData?.phone || '',
 business_name: clientData?.business_name || '',
 currentPassword: '',
 newPassword: '',
 confirmPassword: ''
 });

 // ⭐ Configuración de Pedidos
 const [pedidosConfig, setPedidosConfig] = useState({
 requireCustomerName: localStorage.getItem('pos_require_customer_name') !== 'false',
 takeawayLabel: localStorage.getItem('pos_takeaway_label') || 'Para Llevar',
 kitchenTimer: parseInt(localStorage.getItem('pos_kitchen_timer') || '7'),
 kitchenSound: localStorage.getItem('pos_kitchen_sound') !== 'false',
 kitchenExtraMinutes: parseFloat(localStorage.getItem('pos_kitchen_extra_minutes') || '1.5'),
 });

 const handleSavePedidosConfig = () => {
 localStorage.setItem('pos_require_customer_name', pedidosConfig.requireCustomerName.toString());
 localStorage.setItem('pos_takeaway_label', pedidosConfig.takeawayLabel);
 localStorage.setItem('pos_kitchen_timer', pedidosConfig.kitchenTimer.toString());
 localStorage.setItem('pos_kitchen_sound', pedidosConfig.kitchenSound.toString());
 localStorage.setItem('pos_kitchen_extra_minutes', pedidosConfig.kitchenExtraMinutes.toString());
 showAlert('success', 'Configuración Guardada', ' Las opciones de pedidos se guardaron correctamente');
 };

 // ⭐ Bloqueo de venta sin caja abierta: a diferencia de pedidosConfig (de
 // arriba), esto NO va a localStorage -- es una política de negocio que debe
 // aplicar igual en todos los dispositivos/empleados del negocio, así que
 // vive en el backend (columna clients.require_open_cash_session, ver
 // /api/pos/settings). Se carga al entrar a Configuración y se guarda con
 // su propio botón, independiente del resto de "Opciones de Pedidos".
 const [requireOpenCashSession, setRequireOpenCashSession] = useState(false);
 const [loadingCajaConfig, setLoadingCajaConfig] = useState(true);
 const [savingCajaConfig, setSavingCajaConfig] = useState(false);

 useEffect(() => {
 axios.get(`${API_URL}/api/pos/settings`, { headers: { Authorization: `Bearer ${token}` } })
 .then(res => setRequireOpenCashSession(!!res.data?.require_open_cash_session))
 .catch(() => {})
 .finally(() => setLoadingCajaConfig(false));
 }, [token]);

 const handleSaveCajaConfig = async (nextValue) => {
 setSavingCajaConfig(true);
 try {
 await axios.put(`${API_URL}/api/pos/settings`, { require_open_cash_session: nextValue }, {
 headers: { Authorization: `Bearer ${token}` }
 });
 setRequireOpenCashSession(nextValue);
 showAlert('success', 'Configuración Guardada', nextValue
 ? 'Ahora no se podrá vender en Caja si no hay una caja abierta.'
 : 'Se desactivó el bloqueo: se podrá vender aunque la caja esté cerrada.');
 } catch (error) {
 showAlert('error', 'Error', error.response?.data?.error || 'No se pudo guardar esta opción');
 } finally {
 setSavingCajaConfig(false);
 }
 };

 // ⭐ PALETAS DE COLOR COMPLETAS (6 temas)
 const COLOR_PALETTES = [
 {
 id: 'default',
 name: ' Clásico',
 description: 'Blanco limpio, colores estándar',
 bg: '#f3f4f6',
 preview: 'from-gray-100 to-gray-200',
 vars: { '--color-primary': '#3b82f6', '--color-sidebar': '#ffffff', '--color-accent': '#2563eb', '--color-bg': '#f3f4f6' }
 },
 {
 id: 'dark',
 name: ' Noche Oscura',
 description: 'Fondo oscuro, acentos azules',
 bg: '#1e293b',
 preview: 'from-slate-800 to-slate-900',
 vars: { '--color-primary': '#60a5fa', '--color-sidebar': '#0f172a', '--color-accent': '#3b82f6', '--color-bg': '#1e293b' }
 },
 {
 id: 'emerald',
 name: ' Selva Tropical',
 description: 'Verdes vibrantes, fresco y natural',
 bg: '#ecfdf5',
 preview: 'from-emerald-100 to-green-200',
 vars: { '--color-primary': '#10b981', '--color-sidebar': '#ffffff', '--color-accent': '#059669', '--color-bg': '#ecfdf5' }
 },
 {
 id: 'sunset',
 name: ' Atardecer',
 description: 'Cálidos naranjas y rojos',
 bg: '#fff7ed',
 preview: 'from-orange-100 to-red-200',
 vars: { '--color-primary': '#f97316', '--color-sidebar': '#ffffff', '--color-accent': '#ea580c', '--color-bg': '#fff7ed' }
 },
 {
 id: 'ocean',
 name: ' Océano Profundo',
 description: 'Azules y cianes tranquilos',
 bg: '#eff6ff',
 preview: 'from-blue-100 to-cyan-200',
 vars: { '--color-primary': '#0ea5e9', '--color-sidebar': '#ffffff', '--color-accent': '#0284c7', '--color-bg': '#eff6ff' }
 },
 {
 id: 'purple',
 name: ' Galaxia',
 description: 'Morados profundos y elegantes',
 bg: '#faf5ff',
 preview: 'from-purple-100 to-violet-200',
 vars: { '--color-primary': '#8b5cf6', '--color-sidebar': '#ffffff', '--color-accent': '#7c3aed', '--color-bg': '#faf5ff' }
 },
 ];

 const [selectedPalette, setSelectedPalette] = useState(
 localStorage.getItem('pos_color_palette') || 'default'
 );

 // ⭐ Impresión: ancho de papel para recibos de venta (térmica 58/80mm) y
 // para el reporte de cierre de caja (58/80mm o A4, para negocios que
 // prefieran imprimirlo en una impresora normal de oficina para archivarlo).
 const [receiptWidth, setReceiptWidth] = useState(
 localStorage.getItem('pos_receipt_width') === '58' ? '58' : '80'
 );
 const [closingReportWidth, setClosingReportWidth] = useState(
 ['58', '80', 'a4'].includes(localStorage.getItem('pos_closing_report_width'))
 ? localStorage.getItem('pos_closing_report_width')
 : '80'
 );

 const applyReceiptWidth = (value) => {
 setReceiptWidth(value);
 localStorage.setItem('pos_receipt_width', value);
 showAlert('success', 'Configuración Guardada', ` Los recibos de venta se imprimirán en ${value}mm`);
 };

 const applyClosingReportWidth = (value) => {
 setClosingReportWidth(value);
 localStorage.setItem('pos_closing_report_width', value);
 const label = value === 'a4' ? 'A4' : `${value}mm`;
 showAlert('success', 'Configuración Guardada', ` El reporte de cierre de caja se imprimirá en ${label}`);
 };

 // Estado de colores (solo fondo por ahora - legacy)
 const [colors, setColors] = useState({
 backgroundColor: '#F9FAFB'
 });

 useEffect(() => {
 loadColors();
 }, []);

 const loadColors = async () => {
 try {
 const response = await axios.get(`${API_URL}/api/pos/customization`, {
 headers: { Authorization: `Bearer ${token}` }
 });
 
 if (response.data && response.data.backgroundColor) {
 setColors({ backgroundColor: response.data.backgroundColor });
 // Solo sincronizar con el estado del padre si difiere del localStorage
 const saved = localStorage.getItem('pos_background_color');
 if (!saved && onBgColorChange) onBgColorChange(response.data.backgroundColor);
 }
 } catch (error) {
 console.log('Usando colores por defecto');
 }
 };

 const applyBackgroundColor = (color) => {
 localStorage.setItem('pos_background_color', color);
 if (onBgColorChange) onBgColorChange(color);
 };

 // ⭐ Aplicar paleta completa
 const applyPalette = (palette) => {
 setSelectedPalette(palette.id);
 localStorage.setItem('pos_color_palette', palette.id);
 localStorage.setItem('pos_background_color', palette.bg);
 if (onBgColorChange) onBgColorChange(palette.bg);
 showAlert('success', ' Paleta Aplicada', ` Paleta "${palette.name}" aplicada al POS. Algunos colores se verán al recargar.`);
 };

 const handleSaveProfile = async () => {
 if (!profileData.name.trim()) {
 showAlert('warning', 'Campo Requerido', ' El nombre es requerido');
 return;
 }

 if (!profileData.email.trim()) {
 showAlert('warning', 'Campo Requerido', ' El email es requerido');
 return;
 }

 // Si quiere cambiar contraseña, validar
 if (profileData.newPassword) {
 if (!profileData.currentPassword) {
 showAlert('warning', 'Contraseña Actual Requerida', ' Ingresa tu contraseña actual para cambiarla');
 return;
 }
 if (profileData.newPassword.length < 6) {
 showAlert('warning', 'Contraseña Débil', ' La nueva contraseña debe tener al menos 6 caracteres');
 return;
 }
 if (profileData.newPassword !== profileData.confirmPassword) {
 showAlert('error', 'Contraseñas No Coinciden', ' La nueva contraseña y la confirmación no coinciden');
 return;
 }
 }

 setLoading(true);
 try {
 const dataToSend = {
 name: profileData.name,
 email: profileData.email,
 phone: profileData.phone,
 business_name: profileData.business_name
 };

 // Si hay nueva contraseña, incluirla
 if (profileData.newPassword) {
 dataToSend.currentPassword = profileData.currentPassword;
 dataToSend.newPassword = profileData.newPassword;
 }

 await axios.put(
 `${API_URL}/api/client/profile`,
 dataToSend,
 { headers: { Authorization: `Bearer ${token}` } }
 );

 showAlert('success', 'Perfil Actualizado', ' Tus datos se guardaron exitosamente');

 // Limpiar campos de contraseña
 setProfileData(prev => ({
 ...prev,
 currentPassword: '',
 newPassword: '',
 confirmPassword: ''
 }));

 // ⭐ Notificar al padre que se actualizó el perfil
 if (onProfileUpdate) {
 onProfileUpdate({
 name: dataToSend.name,
 business_name: dataToSend.business_name
 });
 }
 } catch (error) {
 console.error('Error:', error);
 const errorMsg = error.response?.data?.error || 'No se pudo actualizar el perfil';
 showAlert('error', 'Error', ` ${errorMsg}`);
 } finally {
 setLoading(false);
 }
 };

 const handleSaveColors = async () => {
 setLoading(true);
 try {
 await axios.post(
 `${API_URL}/api/pos/customization`,
 colors,
 { headers: { Authorization: `Bearer ${token}` } }
 );

 applyBackgroundColor(colors.backgroundColor);
 showAlert('success', 'Color Guardado', ' El color de fondo se aplicó exitosamente');
 } catch (error) {
 console.error('Error:', error);
 showAlert('error', 'Error', ' No se pudo guardar el color');
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="space-y-6">
 {customAlert && (
 <CustomAlert
 type={customAlert.type}
 title={customAlert.title}
 message={customAlert.message}
 onClose={() => setCustomAlert(null)}
 onConfirm={customAlert.onConfirm}
 zIndex={50}
 />
 )}

 {/* Header */}
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="p-2 bg-blue-100 rounded-lg">
 <Settings className="w-6 h-6 text-blue-600" />
 </div>
 <div>
 <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
 <p className="text-sm text-gray-600">Gestiona tu perfil y personalización</p>
 </div>
 </div>
 </div>

 {/* Tabs */}
 <div className="bg-white rounded-lg shadow">
 <div className="border-b">
 <div className="flex">
 <button
 onClick={() => setActiveTab('perfil')}
 className={`flex-1 px-6 py-4 font-semibold transition ${
 activeTab === 'perfil'
 ? 'border-b-2 border-blue-600 text-blue-600'
 : 'text-gray-600 hover:text-gray-900'
 }`}
 >
 <User className="w-5 h-5 inline mr-2" />
 Mi Perfil
 </button>
 {/* ⭐ Tab Pedidos/Impresión: ocultos en cuentas CRM-only (isCrmOnly), son de POS puro */}
 {!isCrmOnly && (
 <button
 onClick={() => setActiveTab('pedidos')}
 className={`flex-1 px-6 py-4 font-semibold transition ${
 activeTab === 'pedidos'
 ? 'border-b-2 border-orange-600 text-orange-600'
 : 'text-gray-600 hover:text-gray-900'
 }`}
 >
 ️ Pedidos
 </button>
 )}
 {!isCrmOnly && (
 <button
 onClick={() => setActiveTab('impresion')}
 className={`flex-1 px-6 py-4 font-semibold transition ${
 activeTab === 'impresion'
 ? 'border-b-2 border-green-600 text-green-600'
 : 'text-gray-600 hover:text-gray-900'
 }`}
 >
 <Printer className="w-5 h-5 inline mr-2" />
 Impresión
 </button>
 )}
 {/* ⭐ OCULTAR CUSTOMIZACIN SI ES PLAN BASIC */}
 {clientData?.plan !== 'basic' && (
 <button
 onClick={() => setActiveTab('colores')}
 className={`flex-1 px-6 py-4 font-semibold transition ${
 activeTab === 'colores'
 ? 'border-b-2 border-blue-600 text-blue-600'
 : 'text-gray-600 hover:text-gray-900'
 }`}
 >
 <Palette className="w-5 h-5 inline mr-2" />
 Colores
 <span className="ml-2 text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full">Premium</span>
 </button>
 )}
 </div>
 </div>

 <div className="p-6">
 {/* TAB: PERFIL */}
 {activeTab === 'perfil' && (
 <div className="space-y-6 max-w-2xl">
 <h3 className="text-lg font-bold text-gray-900">Información Personal</h3>
 
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 Nombre Completo *
 </label>
 <input
 type="text"
 value={profileData.name}
 onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
 className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
 placeholder="Juan Pérez"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 Email *
 </label>
 <input
 type="email"
 value={profileData.email}
 onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
 className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
 placeholder="correo@ejemplo.com"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 Teléfono
 </label>
 <input
 type="tel"
 value={profileData.phone}
 onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
 className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
 placeholder="0999999999"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 Nombre del Negocio
 </label>
 <input
 type="text"
 value={profileData.business_name}
 onChange={(e) => setProfileData(prev => ({ ...prev, business_name: e.target.value }))}
 className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
 placeholder="Mi Restaurante"
 />
 </div>
 </div>

 <div className="border-t pt-6 mt-6">
 <h3 className="text-lg font-bold text-gray-900 mb-4">Cambiar Contraseña</h3>
 <p className="text-sm text-gray-600 mb-4">
 Deja estos campos vacíos si no deseas cambiar tu contraseña
 </p>

 <div className="space-y-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 Contraseña Actual
 </label>
 <input
 type="password"
 value={profileData.currentPassword}
 onChange={(e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
 className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
 placeholder=""
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 Nueva Contraseña
 </label>
 <input
 type="password"
 value={profileData.newPassword}
 onChange={(e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
 className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
 placeholder=""
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 Confirmar Nueva Contraseña
 </label>
 <input
 type="password"
 value={profileData.confirmPassword}
 onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
 className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
 placeholder=""
 />
 </div>
 </div>
 </div>

 <div className="flex justify-end">
 <button
 onClick={handleSaveProfile}
 disabled={loading}
 className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
 >
 <Save className="w-5 h-5" />
 {loading ? 'Guardando...' : 'Guardar Cambios'}
 </button>
 </div>
 </div>
 )}

 {/* TAB: PEDIDOS */}
 {activeTab === 'pedidos' && (
 <div className="space-y-6 max-w-2xl">
 <h3 className="text-lg font-bold text-gray-900">️ Opciones de Pedidos</h3>

 {/* Nombre del cliente */}
 <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
 <h4 className="font-bold text-orange-900"> Pedidos Para Llevar</h4>
 <div className="flex items-center justify-between">
 <div>
 <p className="font-medium text-gray-700">Nombre del cliente obligatorio</p>
 <p className="text-sm text-gray-500">Si está activo, el cajero debe ingresar nombre antes de cobrar</p>
 </div>
 <button
 onClick={() => setPedidosConfig(p => ({ ...p, requireCustomerName: !p.requireCustomerName }))}
 className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
 pedidosConfig.requireCustomerName ? 'bg-orange-500' : 'bg-gray-300'
 }`}
 >
 <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
 pedidosConfig.requireCustomerName ? 'translate-x-9' : 'translate-x-1'
 }`} />
 </button>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Etiqueta del botón "Para Llevar"</label>
 <input
 type="text"
 value={pedidosConfig.takeawayLabel}
 onChange={(e) => setPedidosConfig(p => ({ ...p, takeawayLabel: e.target.value }))}
 className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
 placeholder="Para Llevar"
 maxLength={20}
 />
 </div>
 </div>

 {/* Caja */}
 <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
 <h4 className="font-bold text-green-900"> Caja</h4>
 <div className="flex items-center justify-between">
 <div>
 <p className="font-medium text-gray-700">Exigir caja abierta para vender</p>
 <p className="text-sm text-gray-500">Si está activo, nadie podrá agregar productos ni cobrar en Caja mientras no haya una caja abierta (aplica a todos los empleados y dispositivos)</p>
 </div>
 <button
 disabled={loadingCajaConfig || savingCajaConfig}
 onClick={() => handleSaveCajaConfig(!requireOpenCashSession)}
 className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors disabled:opacity-50 ${
 requireOpenCashSession ? 'bg-green-500' : 'bg-gray-300'
 }`}
 >
 <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
 requireOpenCashSession ? 'translate-x-9' : 'translate-x-1'
 }`} />
 </button>
 </div>
 </div>

 {/* Módulo Cocina */}
 <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
 <h4 className="font-bold text-blue-900"> Módulo Cocina</h4>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Timer regresivo (minutos)
 </label>
 <div className="flex items-center gap-3">
 <input
 type="range"
 min="1" max="30"
 value={pedidosConfig.kitchenTimer}
 onChange={(e) => setPedidosConfig(p => ({ ...p, kitchenTimer: parseInt(e.target.value) }))}
 className="flex-1"
 />
 <span className="w-16 text-center font-bold text-blue-700 bg-blue-100 px-3 py-1 rounded-lg">
 {pedidosConfig.kitchenTimer} min
 </span>
 </div>
 <p className="text-xs text-gray-500 mt-1">
 Verde: más de la mitad del tiempo | Amarillo: menos de la mitad | Rojo: últimos 2 min
 </p>
 </div>
 <div className="flex items-center justify-between">
 <div>
 <p className="font-medium text-gray-700">Sonido al llegar nuevo pedido</p>
 <p className="text-sm text-gray-500">Reproduce un sonido cuando entra un pedido a cocina</p>
 </div>
 <button
 onClick={() => setPedidosConfig(p => ({ ...p, kitchenSound: !p.kitchenSound }))}
 className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
 pedidosConfig.kitchenSound ? 'bg-blue-500' : 'bg-gray-300'
 }`}
 >
 <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
 pedidosConfig.kitchenSound ? 'translate-x-9' : 'translate-x-1'
 }`} />
 </button>
 </div>

 {/* ⭐ Timer progresivo */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Tiempo extra por pedido en cola (minutos)
 </label>
 <div className="flex items-center gap-3">
 <input
 type="range"
 min="0" max="5" step="0.5"
 value={pedidosConfig.kitchenExtraMinutes}
 onChange={(e) => setPedidosConfig(p => ({ ...p, kitchenExtraMinutes: parseFloat(e.target.value) }))}
 className="flex-1"
 />
 <span className="w-20 text-center font-bold text-blue-700 bg-blue-100 px-3 py-1 rounded-lg text-sm">
 +{pedidosConfig.kitchenExtraMinutes} min
 </span>
 </div>
 <p className="text-xs text-gray-500 mt-1">
 Ejemplo: Base {pedidosConfig.kitchenTimer}min Pedido 1: {pedidosConfig.kitchenTimer}min, Pedido 2: {pedidosConfig.kitchenTimer + pedidosConfig.kitchenExtraMinutes}min, Pedido 3: {pedidosConfig.kitchenTimer + pedidosConfig.kitchenExtraMinutes * 2}min...
 </p>
 </div>
 </div>

 <div className="flex justify-end">
 <button
 onClick={handleSavePedidosConfig}
 className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
 >
 <Save className="w-5 h-5" />
 Guardar Configuración
 </button>
 </div>
 </div>
 )}

 {/* TAB: IMPRESIÓN */}
 {activeTab === 'impresion' && (
 <div className="space-y-6 max-w-2xl">
 <h3 className="text-lg font-bold text-gray-900">Opciones de Impresión</h3>

 <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
 <h4 className="font-bold text-green-900">Recibo de Venta</h4>
 <p className="text-sm text-gray-600">Ancho de papel de la impresora térmica donde se imprime el recibo tras cada cobro.</p>
 <div className="flex gap-3">
 {['80', '58'].map((w) => (
 <button
 key={w}
 onClick={() => applyReceiptWidth(w)}
 className={`flex-1 py-3 rounded-lg font-bold text-sm border-2 transition ${
 receiptWidth === w ? 'border-green-500 bg-green-100 text-green-800' : 'border-gray-300 text-gray-600 hover:border-gray-400'
 }`}
 >
 {w}mm
 </button>
 ))}
 </div>
 </div>

 <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
 <h4 className="font-bold text-blue-900">Reporte de Cierre de Caja</h4>
 <p className="text-sm text-gray-600">
 Formato en el que se imprime el reporte al cerrar caja. A4 es útil si prefieres archivar el cierre en una impresora normal de oficina en vez de la térmica.
 </p>
 <div className="flex gap-3">
 {[{ id: '80', label: '80mm' }, { id: '58', label: '58mm' }, { id: 'a4', label: 'A4' }].map((opt) => (
 <button
 key={opt.id}
 onClick={() => applyClosingReportWidth(opt.id)}
 className={`flex-1 py-3 rounded-lg font-bold text-sm border-2 transition ${
 closingReportWidth === opt.id ? 'border-blue-500 bg-blue-100 text-blue-800' : 'border-gray-300 text-gray-600 hover:border-gray-400'
 }`}
 >
 {opt.label}
 </button>
 ))}
 </div>
 </div>
 </div>
 )}

 {/* ⭐ Tab "IA CRM" ELIMINADO (pedido de Angelo, 2026-08-07): tener la
 base de conocimientos editable acá Y en el nodo de IA de Workflows
 hacía que se mezclaran/confundieran. Ahora el nodo "Responder con IA"
 (WorkflowsManager.js) es el ÚNICO lugar para eso, incluido el
 contador "Mensajes de IA usados hoy" que también vivía acá -- ver
 GET /api/pos/crm/ai-settings, que WorkflowsManager.js sigue usando
 para ese contador. El endpoint PUT sigue existiendo en el backend
 por compatibilidad pero ya no hay ninguna UI que lo llame. */}

 {/* TAB: COLORES (Solo para Premium) */}
 {activeTab === 'colores' && clientData?.plan !== 'basic' && (
 <div className="space-y-6 max-w-3xl">
 <div>
 <h3 className="text-lg font-bold text-gray-900 mb-1"> Paletas de Color del POS</h3>
 <p className="text-sm text-gray-600 mb-2">
 Elige una paleta para personalizar los colores del fondo, botones y acentos de todo el sistema
 </p>
 </div>

 {/* ⭐ GRID DE 6 PALETAS */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {COLOR_PALETTES.map((palette) => {
 const isActive = selectedPalette === palette.id;
 return (
 <div
 key={palette.id}
 className={`rounded-2xl border-2 overflow-hidden cursor-pointer transition-all hover:shadow-lg ${
 isActive ? 'border-blue-500 shadow-lg ring-2 ring-blue-300' : 'border-gray-200 hover:border-gray-400'
 }`}
 onClick={() => applyPalette(palette)}
 >
 {/* Vista previa del color */}
 <div className={`bg-gradient-to-r ${palette.preview} h-20 flex items-center justify-center relative`}>
 {isActive && (
 <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
 ACTIVA
 </div>
 )}
 {/* Mini sidebar preview */}
 <div className="flex gap-2 items-center">
 <div className="w-10 h-12 rounded-lg shadow" style={{ backgroundColor: palette.vars['--color-sidebar'] }}></div>
 <div className="flex flex-col gap-1">
 <div className="w-20 h-2 rounded-full" style={{ backgroundColor: palette.vars['--color-primary'] }}></div>
 <div className="w-16 h-2 rounded-full bg-gray-300 opacity-50"></div>
 <div className="w-12 h-2 rounded-full bg-gray-300 opacity-50"></div>
 </div>
 <div className="w-24 h-12 rounded-lg shadow" style={{ backgroundColor: palette.bg, border: '1px solid #e5e7eb' }}>
 <div className="m-1 h-3 rounded" style={{ backgroundColor: palette.vars['--color-accent'], opacity: 0.7 }}></div>
 </div>
 </div>
 </div>

 {/* Info paleta */}
 <div className="bg-white p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="font-bold text-gray-900">{palette.name}</p>
 <p className="text-xs text-gray-500 mt-0.5">{palette.description}</p>
 </div>
 <div className="flex gap-1">
 <div className="w-5 h-5 rounded-full border border-gray-200 shadow-sm" style={{ backgroundColor: palette.bg }}></div>
 <div className="w-5 h-5 rounded-full border border-gray-200 shadow-sm" style={{ backgroundColor: palette.vars['--color-primary'] }}></div>
 <div className="w-5 h-5 rounded-full border border-gray-200 shadow-sm" style={{ backgroundColor: palette.vars['--color-accent'] }}></div>
 </div>
 </div>
 <button
 onClick={(e) => { e.stopPropagation(); applyPalette(palette); }}
 className={`mt-3 w-full py-2 rounded-lg font-semibold text-sm transition ${
 isActive
 ? 'bg-blue-100 text-blue-700'
 : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
 }`}
 >
 {isActive ? ' Paleta Activa' : 'Aplicar Paleta'}
 </button>
 </div>
 </div>
 );
 })}
 </div>

 {/* Color personalizado */}
 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
 <h4 className="font-bold text-gray-800 mb-3"> Color Personalizado (solo fondo)</h4>
 <div className="flex items-center gap-4">
 <input
 type="color"
 value={colors.backgroundColor}
 onChange={(e) => setColors({ backgroundColor: e.target.value })}
 className="w-16 h-16 rounded-xl border-2 border-gray-300 cursor-pointer"
 />
 <div className="flex-1">
 <input
 type="text"
 value={colors.backgroundColor}
 onChange={(e) => setColors({ backgroundColor: e.target.value })}
 className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono mb-2"
 placeholder="#F9FAFB"
 />
 <button
 onClick={() => { applyBackgroundColor(colors.backgroundColor); showAlert('success', ' Color Aplicado', ' Color personalizado aplicado como fondo'); }}
 className="w-full py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition text-sm"
 >
 Aplicar Color Personalizado
 </button>
 </div>
 </div>
 </div>

 <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
 <p className="text-sm text-blue-800">
 ️ Los cambios de paleta se aplican inmediatamente. Para algunos colores de botones puede ser necesario recargar la página (F5).
 </p>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 );
};

export default Configuracion;
